import { AppIcon } from 'modules/mobileApps/components/AppIcon';
import { MobileApp } from 'modules/mobileApps/types';
import { HelpTooltip, RightTooltip } from 'ui/tooltip';
import { colors } from 'ui/theme';
import { SelectiveTargetingOptionValues } from './SelectiveTargeting.constants';
import {
  AppIconContainer,
  AppsListSubtext,
  HelpIconContainer,
  StyledTooltipContent,
  TooltipContainer,
} from './SelectiveTargeting.styled';
import { SelectiveTargetingOption } from './SelectiveTargeting.types';

export const truncateAppName = (name: string, threshold: number) =>
  name.length <= threshold ? name : `${name.slice(0, threshold)}...`;

export const constructSubtextElements = (
  apps: MobileApp[],
  overflowRequired?: boolean,
  color?: string,
) =>
  apps.map((app, index, array) => (
    <AppsListSubtext key={`app-${index}`} color={color}>
      <AppIconContainer>
        <AppIcon platformType={app.platform} width={14} height={14} color={color} />
      </AppIconContainer>
      <span>{truncateAppName(app.name, 30)}</span>
      <span>{index < array.length - 1 || overflowRequired ? ', ' : ''}</span>
    </AppsListSubtext>
  ));

export const combineSubtextVisibleListWithOverflow = (apps: MobileApp[]) => {
  let subtextBuffer: JSX.Element[] = [];
  const overflow = apps.slice(3);
  const visible = apps.slice(0, 3);

  subtextBuffer = subtextBuffer.concat(
    constructSubtextElements(visible, overflow.length > 0, colors.grey50),
  );

  if (overflow.length > 0) {
    subtextBuffer = subtextBuffer.concat([
      <TooltipContainer key="subtext-tooltip">
        <RightTooltip
          tooltipContent={<div>{constructSubtextElements(overflow, false, colors.white)}</div>}
          isUnderlined
          ContentComponent={StyledTooltipContent}
        >{`+${overflow.length} more`}</RightTooltip>
      </TooltipContainer>,
    ]);
  }

  return subtextBuffer;
};

export const sortApps = (apps: MobileApp[]) =>
  apps.sort((a, b) => {
    const aName = a.name.toUpperCase();
    const bName = b.name.toUpperCase();
    if (aName < bName) {
      return -1;
    }
    if (aName > bName) {
      return 1;
    }
    return 0;
  });

export const getAppsForSelectedOption = (
  selectedOption: SelectiveTargetingOption,
  mobileApps: MobileApp[],
) => {
  if (!selectedOption) {
    return [];
  }

  const optionValues = SelectiveTargetingOptionValues;
  const allMobileApps =
    mobileApps?.filter((app) => app.platform !== 'Web' && app.platform !== 'OTT') || [];
  const allWebsites = mobileApps?.filter((app) => app.platform === 'Web') || [];
  const OTT = mobileApps?.filter((app) => app.platform === 'OTT') || [];

  let apps: MobileApp[];
  switch (selectedOption.value) {
    case optionValues.ALL_MOBILE_APPS:
      apps = allMobileApps;
      break;
    case optionValues.ALL_WEBSITES:
      apps = allWebsites;
      break;
    case optionValues.EVERYTHING:
      apps = mobileApps;
      break;
    case optionValues.OTT:
      apps = OTT;
      break;
    default:
      apps = [];
  }
  return sortApps(apps);
};

export const getAppsListSubtext = (
  selectedOption: SelectiveTargetingOption,
  mobileApps: MobileApp[],
) => combineSubtextVisibleListWithOverflow(getAppsForSelectedOption(selectedOption, mobileApps));

export const getTargetingOptionHelpTooltip = (tooltipContent: string) => {
  return (
    <HelpIconContainer>
      <HelpTooltip
        tooltipContent={tooltipContent}
        iconProps={{
          height: 18,
          width: 18,
        }}
        ContentComponent={StyledTooltipContent}
      />
    </HelpIconContainer>
  );
};
